import Commons from '../../../../Services/Common/Common';

/**
 * SCH_BASIC 스키마
 * - fields: 폼/그리드에서 공통으로 쓰는 필드 정의
 *   - initial: 초기값
 *   - label: 필드명
 *   - required: 필수 입력 여부
 *   - requiredWhen: 언제 필수인지 조건
 *      1. ADMIN: 관리자일 때만 필수
 *
 * - mapper: 저장/삭제 API payload 생성 규칙
 *   - mapRowsBySchema(rows, mapper, context)로 payload를 만듬
 *   - context에는 userInfo 등이 들어오며(mapRowsBySchema에 기본족으로 정의되어 있음),
 *     화면에서 추가로 필요한 값(startDate, endDate 등)을 context로 넘겨줄 수 있다.
 */
export const SCH_BASIC_Schema = {
  fields: {
    // 캘린더 관련 필드
    calId: { initial: '', label: '캘린더 ID' },
    description: { initial: '', label: '캘린더 이름', required: 'Y' },
    color: { initial: '#6aa84f', label: '캘린더 색상' },
    isPublic: { initial: '0', label: '공개 여부' }, // 0: 공개, 1: 비공개
    ownerId: { initial: '', label: '소유자 ID' },
    mainCalendar: { initial: 'N', label: '기본 캘린더 여부' },

    // 일정 관련 필드
    calEId: { initial: 0, label: '일정 ID' },
    calReId: { initial: 0, label: '반복 일정 ID' },
    title: { initial: '', label: '일정 제목', required: 'Y' },
    description: { initial: '', label: '일정 설명' },
    location: { initial: '', label: '장소' },
    startDate: { initial: null, label: '시작 날짜', required: 'Y' },
    endDate: { initial: null, label: '종료 날짜', required: 'Y' },
    isAllDay: { initial: false, label: '하루 종일' },
    isPrivate: { initial: false, label: '비공개 여부' },
    alarmYn: { initial: false, label: '알람 사용 여부' },
    alarms: { initial: [], label: '알람 목록' },

    // 반복 설정 필드
    repeatType: { initial: '', label: '반복 유형' },
    repeatInterval: { initial: 0, label: '반복 간격' },
    repeatDaysOfWeek: { initial: [], label: '반복 요일' },
    repeatMonthlyType: { initial: '', label: '반복 월간 타입' },
    repeatDayOfMonth: { initial: 0, label: '반복 월간 일자' },
    repeatWeekOfMonth: { initial: 0, label: '반복 월간 주차' },
    repeatDayOfWeek: { initial: 0, label: '반복 월간 요일' },
    repeatMonthOfYear: { initial: 0, label: '반복 년간 월' },
    repeatEndType: { initial: '', label: '반복 종료 타입' },
    repeatEndDate: { initial: null, label: '반복 종료 날짜' },
    repeatEndCount: { initial: null, label: '반복 횟수' },

    // 공유 캘린더 관련 필드
    calMId: { initial: 0, label: '공유 캘린더 ID' },
    userId: { initial: '', label: '사용자 ID' },
    searchKeyword: { initial: '', label: '검색 키워드' },
  },

  mapper: {
    // 내 캘린더 조회
    search1: {
      UserId: (_, context) => context.userInfo?.userid || '',
    },

    // 일정 조회
    search2: {
      UserId: (_, context) => context.userInfo?.userid || '',
      StartDt: (_, context) =>
        Commons.EncryptAES(
          context.startDate?.format('YYYY-MM-DD HH:mm:ss') || ''
        ) || '',
      EndDt: (_, context) =>
        Commons.EncryptAES(
          context.endDate?.format('YYYY-MM-DD HH:mm:ss') || ''
        ) || '',
    },

    // 공유 캘린더 조회
    search3: {
      UserId: (_, context) => context.userInfo?.userid || '',
    },

    // 관심 캘린더 추가 시 관리자 조회
    search4: {
      CoCd: (_, context) => Commons.EncryptAES(context.userInfo?.coCd) || '',
      UserId: (_, context) => context.userInfo?.userid || '',
      SearchKeyword: (_, context) =>
        Commons.EncryptAES(context.searchKeyword || '') || '',
    },

    // 공유캘린더 추가 시 공유자의 캘린더ID조회
    search5: {
      UserId: (_, context) =>
        Commons.EncryptAES(context.userId || context.userInfo?.userid) || '',
    },

    // 내일정 보고있는 사람 조회
    search6: {
      UserId: (_, context) => context.userInfo?.userid || '',
    },

    // 일정 저장
    save1: {
      CalId: (row) => Commons.EncryptAES(row.calId || '0') || '',
      CalEId: (row) => Commons.EncryptAES(String(row.calEId || 0)) || '',
      OrganizerId: (_, context) => context.userInfo?.userid || '',
      Title: (row) => Commons.EncryptAES(row.title || '') || '',
      Description: (row) => Commons.EncryptAES(row.description || '') || '',
      Location: (row) => Commons.EncryptAES(row.location || '') || '',
      StartDt: (row, context) =>
        Commons.EncryptAES(
          (row.startDate || context.startDate)?.format('YYYY-MM-DD HH:mm:ss') ||
            ''
        ) || '',
      EndDt: (row, context) =>
        Commons.EncryptAES(
          (row.endDate || context.endDate)?.format('YYYY-MM-DD HH:mm:ss') || ''
        ) || '',
      AllDay: (row) => Commons.EncryptAES(row.isAllDay ? 'Y' : 'N') || '',
      IsCancelled: (row) => Commons.EncryptAES(row.isCancelled || 'N') || '',
      Visibility: (row) => Commons.EncryptAES(row.isPrivate ? '1' : '0') || '',
      CalReId: (row) => Commons.EncryptAES(String(row.calReId || 0)) || '',
      RecurrenceType: (row) =>
        row.RecurrenceType ? Commons.EncryptAES(row.RecurrenceType) : null,
      RecurrenceInterval: (row) =>
        Commons.EncryptAES(String(row.RecurrenceInterval || 0)) || '',
      ByWeekDay: (row) =>
        row.ByWeekDay ? Commons.EncryptAES(row.ByWeekDay) : null,
      ByMonthDay: (row) =>
        Commons.EncryptAES(String(row.ByMonthDay || 0)) || '',
      RruleEndType: (row) =>
        row.RruleEndType ? Commons.EncryptAES(row.RruleEndType) : null,
      RecurrenceCount: (row) =>
        Commons.EncryptAES(String(row.RecurrenceCount || 0)) || '',
      RecurrenceUntil: (row) =>
        row.RecurrenceUntil ? Commons.EncryptAES(row.RecurrenceUntil) : null,
      AlarmMinutesBefore: (row) =>
        row.AlarmMinutesBefore
          ? Commons.EncryptAES(String(row.AlarmMinutesBefore))
          : null,
      UserId: (_, context) => context.userInfo?.userid || '',
      AlarmId: (row) => Commons.EncryptAES(String(row.alarmId || 0)) || '',
    },

    // 내캘린더, 관심캘린더 체크박스 이벤트
    save2: {
      CalId: (row) => Commons.EncryptAES(row.calId || '0') || '',
      Gubun: (row) => Commons.EncryptAES(row.gubun || '0') || '',
      UserId: (row) => Commons.EncryptAES(row.userId || '') || '',
      UpdateId: (_, context) => context.userInfo?.userid || '',
      IsPublic: (row) => Commons.EncryptAES(row.isPublic || '0') || '',
      CalMId: (row) => Commons.EncryptAES(row.calMId || '0') || '',
    },

    // 내캘린더 저장/수정
    save3: {
      CalId: (row) => Commons.EncryptAES(row.calId || '0') || '',
      OwnerId: (_, context) => context.userInfo?.userid || '',
      Description: (row) => Commons.EncryptAES(row.description || '') || '',
      Color: (row) => Commons.EncryptAES(row.color || '#6aa84f') || '',
      IsPublic: (row) => Commons.EncryptAES(row.isPublic || '0') || '',
      UserId: (_, context) => context.userInfo?.userid || '',
    },

    // 관심캘린더 저장/수정
    save4: {
      CalMId: (row) => Commons.EncryptAES(String(row.calMId || 0)) || '',
      CalId: (row) => Commons.EncryptAES(row.calId || '0') || '',
      UserId: (row) => Commons.EncryptAES(row.userId || '') || '',
      Color: (row) => Commons.EncryptAES(row.color || '#6aa84f') || '',
      UpdateId: (_, context) => context.userInfo?.userid || '',
    },

    // 내일정을 보고 있는 동료 신청 수락
    save5: {
      CalMId: (row) => Commons.EncryptAES(String(row.calMId || 0)) || '',
      UserId: (_, context) => context.userInfo?.userid || '',
    },

    // 내캘린더 삭제
    delete1: {
      CalId: (row) => Commons.EncryptAES(row.calId || '') || '',
    },

    // 공유캘린더 삭제
    delete2: {
      CalMId: (row) => Commons.EncryptAES(String(row.calMId || 0)) || '',
    },

    // 이벤트 삭제
    delete3: {
      CalEId: (row) => Commons.EncryptAES(String(row.calEId || 0)) || '',
      UserId: (_, context) => context.userInfo?.userid || '',
    },
  },
};
